import React, { useState, useEffect } from 'react';
import { saveAs } from 'file-saver';
import {
  FiCalendar,
  FiSearch,
  FiDownload,
  FiRefreshCw,
  FiFileText,
  FiMail,
  FiPrinter,
  FiFilter,
  FiDownloadCloud,
  FiCheckCircle,
  FiCopy,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiX,
  FiLoader,
  FiTrendingUp,
  FiUsers,
  FiFile,
  FiSend,
  FiBarChart2,
  FiAlertCircle,
  FiHome,
  FiMapPin,
  FiPhone,
  FiMail as FiMailIcon,
  FiInfo,
  FiCreditCard,
  FiCoffee,
} from 'react-icons/fi';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL || 'http://localhost:9000';

function GSTReportsModule() {
  // Module Type: 'hotel' or 'restaurant'
  const [moduleType, setModuleType] = useState('hotel');

  // UI State
  const [activeTab, setActiveTab] = useState('B2B');
  const [showFilters, setShowFilters] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [summaryData, setSummaryData] = useState(null);

  // Date State
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });

  // Filter State
  const [filters, setFilters] = useState({
    gstin: '',
    gstRate: '',
    companyId: '',
    invoiceNo: '',
    paymentStatus: '',
    orderType: '',
  });

  // Email State
  const [emailData, setEmailData] = useState({
    recipients: '',
    subject: 'GST Returns Report - Pagoda Executive',
    message:
      'Dear Team,\n\nPlease find attached the GST returns report for the selected period.\n\nRegards,\nAccounts Team\nPagoda Executive',
    format: 'xlsx',
    reportTypes: ['B2B', 'B2C', 'B2C_DETAIL'],
  });

  // Loading States
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Data States
  const [reportData, setReportData] = useState(null);
  const [companies, setCompanies] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Hotel Info
  const hotelInfo = {
    name: 'The Pagoda Executive',
    gstin: '27AAACP1234K1Z5',
    address: 'Lote MIDC, Chiplun - 415722, Maharashtra',
    contact: '+91 8698732336',
    email: 'pagodamain@gmail.com',
    stateCode: '27',
  };

  // Fetch Companies
  useEffect(() => {
    fetchCompanies();
  }, []);

  // Fetch Report Data on filter change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReportData();
    }, 500);
    return () => clearTimeout(timer);
  }, [
    startDate,
    endDate,
    filters.invoiceNo,
    filters.gstin,
    filters.gstRate,
    filters.companyId,
    filters.paymentStatus,
    filters.orderType,
    moduleType,
    activeTab,
  ]);

  // Reset page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, moduleType]);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/companies/list`);
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchSummaryData = async () => {
    try {
      let url;
      if (moduleType === 'hotel') {
        url = new URL(`${BQ_BASE_URL}/bq/api/gst-reports/summary`);
      } else {
        url = new URL(`${BQ_BASE_URL}/bq/api/restaurant-gst-reports/summary`);
      }

      if (startDate) url.searchParams.append('start_date', startDate);
      if (endDate) url.searchParams.append('end_date', endDate);
      if (filters.invoiceNo) {
        const invoiceSearch = filters.invoiceNo.trim();
        url.searchParams.append('invoice_number', encodeURIComponent(invoiceSearch));
      }
      if (filters.gstin) url.searchParams.append('customer_gst', filters.gstin);
      if (filters.gstRate) url.searchParams.append('gst_rate', parseFloat(filters.gstRate));
      if (filters.paymentStatus && moduleType === 'hotel')
        url.searchParams.append('payment_status', filters.paymentStatus);
      if (filters.companyId && moduleType === 'hotel')
        url.searchParams.append('company_id', filters.companyId);
      if (filters.orderType && moduleType === 'restaurant')
        url.searchParams.append('order_type', filters.orderType);

      url.searchParams.append('page', '1');
      url.searchParams.append('page_size', '100');

      const response = await fetch(url.toString());

      if (response.ok) {
        const data = await response.json();
        if (moduleType === 'hotel') {
          setSummaryData({
            grand_total: data.grand_total || 0,
            total_taxable_value: data.total_taxable_value || 0,
            total_gst: data.total_gst || 0,
            total_cgst: data.total_cgst || 0,
            total_sgst: data.total_sgst || 0,
            total_orders: data.total_invoices || 0,
            b2b_orders: data.b2b_invoices || 0,
            b2c_orders: data.b2c_invoices || 0,
          });
        } else {
          setSummaryData(data);
        }
      }
    } catch (error) {
      console.error('Error fetching summary data:', error);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      let url;
      if (moduleType === 'hotel') {
        url = new URL(`${BQ_BASE_URL}/bq/api/gst-reports/preview`);
      } else {
        url = new URL(`${BQ_BASE_URL}/bq/api/restaurant-gst-reports/preview`);
      }

      if (startDate) url.searchParams.append('start_date', startDate);
      if (endDate) url.searchParams.append('end_date', endDate);

      if (filters.invoiceNo) {
        const invoiceSearch = filters.invoiceNo.trim();
        url.searchParams.append('invoice_number', encodeURIComponent(invoiceSearch));
      }

      if (filters.gstin) url.searchParams.append('customer_gst', filters.gstin);
      if (filters.gstRate) url.searchParams.append('gst_rate', parseFloat(filters.gstRate));
      if (filters.paymentStatus && moduleType === 'hotel')
        url.searchParams.append('payment_status', filters.paymentStatus);
      if (filters.companyId && moduleType === 'hotel')
        url.searchParams.append('company_id', filters.companyId);
      if (filters.orderType && moduleType === 'restaurant')
        url.searchParams.append('order_type', filters.orderType);

      if (moduleType !== 'hotel') {
        url.searchParams.append('report_type', activeTab);
      }

      url.searchParams.append('page', '1');
      url.searchParams.append('page_size', '500');

      const response = await fetch(url.toString());

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      setReportData(data);
      fetchSummaryData();
    } catch (error) {
      console.error('Error fetching report data:', error);
      alert('Failed to fetch report data. Please check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  // Get filtered rows with client-side invoice number filtering
  const getFilteredRows = () => {
    if (!reportData) return [];

    let rows = [];

    if (moduleType === 'hotel') {
      switch (activeTab) {
        case 'B2B':
          rows = reportData.b2b_data?.service_rate_groups?.flatMap((g) => g.rows) || [];
          break;
        case 'B2C':
          rows = reportData.b2c_data?.rows || [];
          break;
        case 'B2C_DETAIL':
          rows = reportData.b2c_detail_data?.service_rate_groups?.flatMap((g) => g.rows) || [];
          break;
        default:
          rows = [];
      }
    } else {
      switch (activeTab) {
        case 'B2B':
          rows = reportData.b2b_data?.service_rate_groups?.flatMap((g) => g.rows) || [];
          break;
        case 'B2C':
          rows = reportData.b2c_data?.rows || [];
          break;
        case 'B2C_DETAIL':
          rows = reportData.b2c_detail_data?.service_rate_groups?.flatMap((g) => g.rows) || [];
          break;
        default:
          rows = [];
      }
    }

    // Client-side filtering for invoice number
    if (filters.invoiceNo && filters.invoiceNo.trim() !== '') {
      const searchTerm = filters.invoiceNo.trim().toLowerCase();
      rows = rows.filter((row) => {
        const invoiceNumber = (row.invoice_number || '').toLowerCase();
        return invoiceNumber.includes(searchTerm);
      });
    }

    return rows;
  };

  // Get current rows with pagination
  const getCurrentRows = () => {
    return getFilteredRows();
  };

  // Get current summary with filter applied
  const getCurrentSummary = () => {
    if (!reportData) return {};

    let summary = {};
    if (moduleType === 'hotel') {
      switch (activeTab) {
        case 'B2B':
          summary = reportData.b2b_data || {};
          break;
        case 'B2C':
          summary = reportData.b2c_data || {};
          break;
        case 'B2C_DETAIL':
          summary = reportData.b2c_detail_data || {};
          break;
        default:
          summary = {};
      }
    } else {
      switch (activeTab) {
        case 'B2B':
          summary = reportData.b2b_data || {};
          break;
        case 'B2C':
          summary = reportData.b2c_data || {};
          break;
        case 'B2C_DETAIL':
          summary = reportData.b2c_detail_data || {};
          break;
        default:
          summary = {};
      }
    }

    // Apply client-side filtering to summary totals
    if (filters.invoiceNo && filters.invoiceNo.trim() !== '') {
      const filteredRows = getFilteredRows();
      const totals = filteredRows.reduce((acc, row) => {
        acc.total_taxable_value =
          (acc.total_taxable_value || 0) + (parseFloat(row.taxable_value) || 0);
        acc.total_cgst = (acc.total_cgst || 0) + (parseFloat(row.cgst) || 0);
        acc.total_sgst = (acc.total_sgst || 0) + (parseFloat(row.sgst) || 0);
        acc.grand_total =
          (acc.grand_total || 0) + (parseFloat(row.total_inv) || parseFloat(row.total) || 0);
        return acc;
      }, {});

      summary.total_taxable_value = totals.total_taxable_value || 0;
      summary.total_cgst = totals.total_cgst || 0;
      summary.total_sgst = totals.total_sgst || 0;
      summary.grand_total = totals.grand_total || 0;
      summary.no_of_invoices = filteredRows.length;
    }

    return summary;
  };

  const currentRows = getCurrentRows();
  const totalPages = Math.ceil(currentRows.length / itemsPerPage);
  const paginatedRows = currentRows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const goToPage = (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);
  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);

  const downloadReport = async (format = 'xlsx') => {
    setLoading(true);
    try {
      let url;
      if (moduleType === 'hotel') {
        url = new URL(`${BQ_BASE_URL}/bq/api/gst-reports/generate`);
      } else {
        url = new URL(`${BQ_BASE_URL}/bq/api/restaurant-gst-reports/generate`);
      }

      url.searchParams.append('export_format', format);

      if (startDate) url.searchParams.append('start_date', startDate);
      if (endDate) url.searchParams.append('end_date', endDate);
      if (filters.invoiceNo) {
        const invoiceSearch = filters.invoiceNo.trim();
        url.searchParams.append('invoice_number', encodeURIComponent(invoiceSearch));
      }
      if (activeTab !== 'ALL' && moduleType !== 'restaurant')
        url.searchParams.append('report_type', activeTab);
      if (moduleType === 'restaurant') url.searchParams.append('report_type', activeTab);
      if (filters.gstin) url.searchParams.append('customer_gst', filters.gstin);
      if (filters.gstRate) url.searchParams.append('gst_rate', parseFloat(filters.gstRate));
      if (filters.paymentStatus && moduleType === 'hotel')
        url.searchParams.append('payment_status', filters.paymentStatus);
      if (filters.companyId && moduleType === 'hotel')
        url.searchParams.append('company_id', filters.companyId);
      if (filters.orderType && moduleType === 'restaurant')
        url.searchParams.append('order_type', filters.orderType);

      url.searchParams.append('page', '1');
      url.searchParams.append('page_size', '500');

      const response = await fetch(url.toString());

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const blob = await response.blob();
      const filename = `${moduleType.toUpperCase()}_GST_${activeTab}_${startDate}_to_${endDate}.${format}`;
      saveAs(blob, filename);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Failed to download report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadCombinedReport = async () => {
    setLoading(true);
    try {
      let url;
      if (moduleType === 'hotel') {
        url = new URL(`${BQ_BASE_URL}/bq/api/gst-reports/generate-combined`);
      } else {
        url = new URL(`${BQ_BASE_URL}/bq/api/restaurant-gst-reports/generate-combined`);
      }

      if (startDate) url.searchParams.append('start_date', startDate);
      if (endDate) url.searchParams.append('end_date', endDate);
      if (filters.invoiceNo) {
        const invoiceSearch = filters.invoiceNo.trim();
        url.searchParams.append('invoice_number', encodeURIComponent(invoiceSearch));
      }
      if (filters.gstin) url.searchParams.append('customer_gst', filters.gstin);
      if (filters.gstRate) url.searchParams.append('gst_rate', parseFloat(filters.gstRate));
      if (filters.paymentStatus && moduleType === 'hotel')
        url.searchParams.append('payment_status', filters.paymentStatus);
      if (filters.companyId && moduleType === 'hotel')
        url.searchParams.append('company_id', filters.companyId);
      if (filters.orderType && moduleType === 'restaurant')
        url.searchParams.append('order_type', filters.orderType);

      url.searchParams.append('page', '1');
      url.searchParams.append('page_size', '500');

      const response = await fetch(url.toString());

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const blob = await response.blob();
      const filename = `${moduleType.toUpperCase()}_GST_Combined_${startDate}_to_${endDate}.xlsx`;
      saveAs(blob, filename);
    } catch (error) {
      console.error('Error downloading combined report:', error);
      alert('Failed to download combined report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendEmailReport = async () => {
    if (!emailData.recipients.trim()) {
      alert('Please enter at least one recipient email address');
      return;
    }

    setSendingEmail(true);
    const recipientList = emailData.recipients
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e);

    const emailFilters = {};
    if (startDate) emailFilters.start_date = startDate;
    if (endDate) emailFilters.end_date = endDate;
    if (filters.invoiceNo) emailFilters.invoice_number = filters.invoiceNo.trim();
    if (filters.gstin) emailFilters.customer_gst = filters.gstin;
    if (filters.gstRate) emailFilters.gst_rate = parseFloat(filters.gstRate);
    if (filters.companyId && moduleType === 'hotel') emailFilters.company_id = filters.companyId;
    if (filters.orderType && moduleType === 'restaurant')
      emailFilters.order_type = filters.orderType;

    const payload = {
      recipients: recipientList,
      subject: emailData.subject,
      message: emailData.message,
      export_format: emailData.format,
      report_types: emailData.reportTypes,
      filters: emailFilters,
    };

    try {
      const url =
        moduleType === 'hotel'
          ? `${BQ_BASE_URL}/bq/api/gst-reports/send-email`
          : `${BQ_BASE_URL}/bq/api/restaurant-gst-reports/send-email`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        setEmailSent(true);
        setShowEmailModal(false);
        setEmailData({
          ...emailData,
          recipients: '',
        });
        setTimeout(() => setEmailSent(false), 3000);
      } else {
        alert(`Failed to send email: ${result.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const copySummary = () => {
    const summary = summaryData || {};
    const summaryText = `═══════════════════════════════════════════════════════════════════════════════
                         PAGODA EXECUTIVE - GST RETURN SUMMARY                 
═══════════════════════════════════════════════════════════════════════════════
 Module: ${moduleType.toUpperCase()}                                                                
 Period: ${startDate} to ${endDate}                                                  
═══════════════════════════════════════════════════════════════════════════════
 💰 GRAND TOTAL: ₹${summary.grand_total?.toLocaleString() || '0'}                       
 📊 Total Taxable Value: ₹${summary.total_taxable_value?.toLocaleString() || '0'}                         
 💰 Total GST: ₹${summary.total_gst?.toLocaleString() || '0'}                                  
   • CGST: ₹${summary.total_cgst?.toLocaleString() || '0'}                                  
   • SGST: ₹${summary.total_sgst?.toLocaleString() || '0'}                                  
 📄 Total Invoices: ${summary.total_orders?.toLocaleString() || '0'}                                                 
═══════════════════════════════════════════════════════════════════════════════
 Generated from Pagoda Executive ERP System on ${new Date().toLocaleString()}`;

    navigator.clipboard.writeText(summaryText);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const renderSummaryCards = () => {
    const summary = summaryData || {};

    const cards = [
      {
        title: 'Grand Total',
        value: `₹${summary.grand_total?.toLocaleString() || '0'}`,
        subtext: 'Including all taxes',
        icon: <FiFile className="text-[#C9A84C] text-xl" />,
      },
      {
        title: 'Total Taxable Value',
        value: `₹${summary.total_taxable_value?.toLocaleString() || '0'}`,
        subtext: 'Value before tax',
        icon: <FiTrendingUp className="text-[#C9A84C] text-xl" />,
      },
      {
        title: 'Total GST',
        value: `₹${summary.total_gst?.toLocaleString() || '0'}`,
        subtext: `CGST: ₹${summary.total_cgst?.toFixed(2) || '0'} | SGST: ₹${summary.total_sgst?.toFixed(2) || '0'}`,
        icon: <FiBarChart2 className="text-[#C9A84C] text-xl" />,
      },
      {
        title: 'Total Orders/Invoices',
        value: summary.total_orders?.toLocaleString() || '0',
        subtext:
          moduleType === 'hotel'
            ? `B2B: ${summary.b2b_orders || 0} | B2C: ${summary.b2c_orders || 0}`
            : `Restaurant Orders`,
        icon: <FiCreditCard className="text-[#C9A84C] text-xl" />,
      },
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        {cards.map((card, idx) => (
          <div
            key={idx}
            className="bg-white rounded-xl shadow-md p-5 border border-[#E8ECF2] hover:shadow-lg transition-shadow"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-[#6B7FA0] uppercase tracking-wide font-semibold">
                  {card.title}
                </p>
                <p className="text-2xl font-bold text-[#0F1A3A] mt-2">{card.value}</p>
                <p className="text-xs text-[#6B7FA0] mt-2">{card.subtext}</p>
              </div>
              <div className="bg-[#F0F2F5] p-3 rounded-xl">{card.icon}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderB2BTable = () => {
    const summary = getCurrentSummary();
    const filteredRows = getFilteredRows();
    const hasInvoiceFilter = filters.invoiceNo && filters.invoiceNo.trim() !== '';

    return (
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-[#E8ECF2]">
        <div className="px-6 py-4 bg-[#0F1A3A]">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-white font-semibold">B2B Invoice Details</h3>
              <p className="text-[#8A9FC0] text-xs mt-1">
                Date Range: {summary.date_range || `${startDate} to ${endDate}`}
              </p>
              {hasInvoiceFilter && (
                <p className="text-[#C9A84C] text-xs mt-1">
                  🔍 Filtered by Invoice Number: "{filters.invoiceNo.trim()}" -{' '}
                  {filteredRows.length} results found
                </p>
              )}
            </div>
            <div className="flex space-x-2">
              <span className="px-3 py-1 bg-[#C9A84C] text-[#0F1A3A] text-xs rounded-full font-medium">
                Recipients: {summary.no_of_recipients || 0}
              </span>
              <span className="px-3 py-1 bg-[#1B2F5C] text-white text-xs rounded-full">
                Invoices: {summary.no_of_invoices || 0}
              </span>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E8ECF2]">
            <thead className="bg-[#F8F9FB]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  GSTIN
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Receiver Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Invoice No
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Service Type
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Rate
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Taxable Value
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  CGST
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  SGST
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8ECF2]">
              {paginatedRows.map((row, idx) => (
                <tr key={idx} className="hover:bg-[#F8F9FB] transition-colors">
                  <td className="px-4 py-3 text-sm font-mono text-[#0F1A3A]">
                    {row.gstin_uin || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#4A5F7A]">
                    {row.receiver_name || row.order_type_label || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-[#0F1A3A]">
                    {row.invoice_number || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#4A5F7A]">{row.invoice_date || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 bg-[#F0F2F5] rounded-full text-xs text-[#4A5F7A]">
                      {row.service_type || row.order_type_label || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-[#0F1A3A]">
                    {row.rate || 0}%
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
                    ₹{row.taxable_value?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
                    ₹{row.cgst?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
                    ₹{row.sgst?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-[#0F1A3A]">
                    ₹{row.total_inv?.toFixed(2) || row.total?.toFixed(2) || '0.00'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#F8F9FB] font-semibold">
              <tr>
                <td colSpan="5" className="px-4 py-3 text-right text-[#0F1A3A]">
                  Total:
                </td>
                <td className="px-4 py-3 text-right text-[#0F1A3A]">
                  ₹{summary.total_taxable_value?.toFixed(2) || '0.00'}
                </td>
                <td className="px-4 py-3 text-right text-[#0F1A3A]">
                  ₹{summary.total_cgst?.toFixed(2) || '0.00'}
                </td>
                <td className="px-4 py-3 text-right text-[#0F1A3A]">
                  ₹{summary.total_sgst?.toFixed(2) || '0.00'}
                </td>
                <td className="px-4 py-3 text-right text-[#0F1A3A]">
                  ₹
                  {summary.total_invoice_value?.toFixed(2) ||
                    summary.grand_total?.toFixed(2) ||
                    '0.00'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  const renderB2CTable = () => {
    const summary = getCurrentSummary();
    const filteredRows = getFilteredRows();
    const hasInvoiceFilter = filters.invoiceNo && filters.invoiceNo.trim() !== '';

    return (
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-[#E8ECF2]">
        <div className="px-6 py-4 bg-[#0F1A3A]">
          <div>
            <h3 className="text-white font-semibold">B2C Summary (Consumer Sales)</h3>
            <p className="text-[#8A9FC0] text-xs mt-1">
              Date Range: {summary.date_range || `${startDate} to ${endDate}`}
            </p>
            {hasInvoiceFilter && (
              <p className="text-[#C9A84C] text-xs mt-1">
                🔍 Filtered by Invoice Number: "{filters.invoiceNo.trim()}" - {filteredRows.length}{' '}
                results found
              </p>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E8ECF2]">
            <thead className="bg-[#F8F9FB]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Category/Place
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Place of Supply
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Rate
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Taxable Value
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  CGST
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  SGST
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8ECF2]">
              {paginatedRows.map((row, idx) => (
                <tr key={idx} className="hover:bg-[#F8F9FB]">
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 text-[#0F1A3A] rounded-full text-xs bg-[#F0F2F5]">
                      {row.order_type_label || row.row_type || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#4A5F7A]">{row.category || ''}</td>
                  <td className="px-4 py-3 text-sm text-[#4A5F7A]">
                    {row.place_of_supply || '27-Maharashtra'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-[#0F1A3A]">
                    {row.rate || 0}%
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
                    ₹{row.taxable_value?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
                    ₹{row.cgst?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
                    ₹{row.sgst?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-[#0F1A3A]">
                    ₹{row.total?.toFixed(2) || '0.00'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#F8F9FB] font-semibold">
              <tr>
                <td colSpan="3" className="px-4 py-3 text-right text-[#0F1A3A]">
                  Total:
                </td>
                <td className="px-4 py-3 text-right text-[#0F1A3A]">
                  ₹{summary.total_taxable_value?.toFixed(2) || '0.00'}
                </td>
                <td className="px-4 py-3 text-right text-[#0F1A3A]">
                  ₹{summary.total_cgst?.toFixed(2) || '0.00'}
                </td>
                <td className="px-4 py-3 text-right text-[#0F1A3A]">
                  ₹{summary.total_sgst?.toFixed(2) || '0.00'}
                </td>
                <td className="px-4 py-3 text-right text-[#0F1A3A]">
                  ₹{summary.grand_total?.toFixed(2) || '0.00'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  const renderB2CDetailTable = () => {
    const summary = getCurrentSummary();
    const filteredRows = getFilteredRows();
    const hasInvoiceFilter = filters.invoiceNo && filters.invoiceNo.trim() !== '';

    return (
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-[#E8ECF2]">
        <div className="px-6 py-4 bg-[#0F1A3A]">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-white font-semibold">B2C Detailed Invoice Report</h3>
              <p className="text-[#8A9FC0] text-xs mt-1">
                Date Range: {summary.date_range || `${startDate} to ${endDate}`}
              </p>
              {hasInvoiceFilter && (
                <p className="text-[#C9A84C] text-xs mt-1">
                  🔍 Filtered by Invoice Number: "{filters.invoiceNo.trim()}" -{' '}
                  {filteredRows.length} results found
                </p>
              )}
            </div>
            <span className="px-3 py-1 bg-[#C9A84C] text-[#0F1A3A] text-xs rounded-full font-medium">
              Transactions: {summary.no_of_invoices || summary.no_of_orders || 0}
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E8ECF2]">
            <thead className="bg-[#F8F9FB]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  GSTIN
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Category/Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Invoice No
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Rate
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Taxable Value
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  CGST
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  SGST
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8ECF2]">
              {paginatedRows.map((row, idx) => (
                <tr key={idx} className="hover:bg-[#F8F9FB]">
                  <td className="px-4 py-3 text-sm font-mono text-[#0F1A3A]">
                    {row.gstin_uin || 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 bg-[#F0F2F5] rounded-full text-xs text-[#4A5F7A]">
                      {row.category || row.order_type_label || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-[#0F1A3A]">
                    {row.invoice_number || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#4A5F7A]">{row.invoice_date || '-'}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-[#0F1A3A]">
                    {row.rate || 0}%
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
                    ₹{row.taxable_value?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
                    ₹{row.cgst?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
                    ₹{row.sgst?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-[#0F1A3A]">
                    ₹{row.total_inv?.toFixed(2) || row.total?.toFixed(2) || '0.00'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#F8F9FB] font-semibold">
              <tr>
                <td colSpan="4" className="px-4 py-3 text-right text-[#0F1A3A]">
                  Total:
                </td>
                <td className="px-4 py-3 text-right text-[#0F1A3A]">
                  ₹{summary.total_taxable_value?.toFixed(2) || '0.00'}
                </td>
                <td className="px-4 py-3 text-right text-[#0F1A3A]">
                  ₹{summary.total_cgst?.toFixed(2) || '0.00'}
                </td>
                <td className="px-4 py-3 text-right text-[#0F1A3A]">
                  ₹{summary.total_sgst?.toFixed(2) || '0.00'}
                </td>
                <td className="px-4 py-3 text-right text-[#0F1A3A]">
                  ₹
                  {summary.total_invoice_value?.toFixed(2) ||
                    summary.grand_total?.toFixed(2) ||
                    '0.00'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
      let endPage = Math.min(totalPages, startPage + maxVisible - 1);

      if (endPage - startPage + 1 < maxVisible) {
        startPage = Math.max(1, endPage - maxVisible + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="bg-white rounded-xl shadow-lg px-6 py-4 mt-6 flex items-center justify-between flex-wrap gap-4 border border-[#E8ECF2]">
        <div className="text-sm text-[#6B7FA0]">
          Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
          {Math.min(currentPage * itemsPerPage, currentRows.length)} of {currentRows.length} entries
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={goToFirstPage}
            disabled={currentPage === 1}
            className={`p-2 border border-[#E8ECF2] rounded-lg transition ${
              currentPage === 1
                ? 'text-[#D0D5DD] cursor-not-allowed'
                : 'hover:bg-[#F8F9FB] text-[#0F1A3A]'
            }`}
          >
            <FiChevronsLeft className="h-4 w-4" />
          </button>
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className={`p-2 border border-[#E8ECF2] rounded-lg transition ${
              currentPage === 1
                ? 'text-[#D0D5DD] cursor-not-allowed'
                : 'hover:bg-[#F8F9FB] text-[#0F1A3A]'
            }`}
          >
            <FiChevronLeft className="h-4 w-4" />
          </button>

          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => goToPage(page)}
              className={`px-3 py-1.5 border rounded-lg text-sm transition ${
                currentPage === page
                  ? 'bg-[#0F1A3A] text-white border-[#0F1A3A]'
                  : 'hover:bg-[#F8F9FB] text-[#0F1A3A] border-[#E8ECF2]'
              }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className={`p-2 border border-[#E8ECF2] rounded-lg transition ${
              currentPage === totalPages
                ? 'text-[#D0D5DD] cursor-not-allowed'
                : 'hover:bg-[#F8F9FB] text-[#0F1A3A]'
            }`}
          >
            <FiChevronRight className="h-4 w-4" />
          </button>
          <button
            onClick={goToLastPage}
            disabled={currentPage === totalPages}
            className={`p-2 border border-[#E8ECF2] rounded-lg transition ${
              currentPage === totalPages
                ? 'text-[#D0D5DD] cursor-not-allowed'
                : 'hover:bg-[#F8F9FB] text-[#0F1A3A]'
            }`}
          >
            <FiChevronsRight className="h-4 w-4" />
          </button>
        </div>
        <div className="text-sm text-[#6B7FA0]">
          Page {currentPage} of {totalPages}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!reportData) {
      return (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl shadow-lg border border-[#E8ECF2]">
          <FiFileText className="text-[#D0D5DD] text-6xl mb-4" />
          <p className="text-[#6B7FA0]">Select date range and click Generate to view report</p>
        </div>
      );
    }

    if (currentRows.length === 0) {
      return (
        <div className="text-center py-16 bg-white rounded-xl shadow-lg border border-[#E8ECF2]">
          <FiAlertCircle className="h-16 w-16 mx-auto text-[#D0D5DD] mb-4" />
          <p className="text-[#6B7FA0]">No data found for the selected period</p>
          <p className="text-sm text-[#D0D5DD] mt-1">Try adjusting your date range or filters</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'B2B':
        return renderB2BTable();
      case 'B2C':
        return renderB2CTable();
      case 'B2C_DETAIL':
        return renderB2CDetailTable();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6FA]">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 shadow-xl flex items-center space-x-3 border border-[#E8ECF2]">
            <FiLoader className="h-6 w-6 animate-spin text-[#C9A84C]" />
            <span className="text-[#0F1A3A]">Loading report data...</span>
          </div>
        </div>
      )}

      {/* Notifications */}
      {emailSent && (
        <div className="fixed top-4 right-4 bg-[#0F1A3A] text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center">
          <FiCheckCircle className="h-5 w-5 mr-2 text-[#C9A84C]" />
          Email sent successfully!
        </div>
      )}

      {copySuccess && (
        <div className="fixed top-4 right-4 bg-[#0F1A3A] text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center">
          <FiCheckCircle className="h-5 w-5 mr-2 text-[#C9A84C]" />
          Summary copied to clipboard!
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#E8ECF2]">
            <div className="sticky top-0 bg-white border-b border-[#E8ECF2] px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-[#0F1A3A]">Send GST Report via Email</h3>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-2 hover:bg-[#F8F9FB] rounded-lg text-[#6B7FA0]"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0F1A3A] mb-1">
                  Recipients *
                </label>
                <input
                  type="text"
                  value={emailData.recipients}
                  onChange={(e) => setEmailData({ ...emailData, recipients: e.target.value })}
                  placeholder="email1@example.com, email2@example.com"
                  className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
                />
                <p className="text-xs text-[#6B7FA0] mt-1">Separate multiple emails with commas</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F1A3A] mb-1">Subject</label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F1A3A] mb-1">Message</label>
                <textarea
                  rows="4"
                  value={emailData.message}
                  onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0F1A3A] mb-1">Format</label>
                  <select
                    value={emailData.format}
                    onChange={(e) => setEmailData({ ...emailData, format: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
                  >
                    <option value="xlsx">Excel (.xlsx)</option>
                    <option value="pdf">PDF (.pdf)</option>
                    <option value="csv">CSV (.csv)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F1A3A] mb-1">
                    Report Types
                  </label>
                  <select
                    multiple
                    value={emailData.reportTypes}
                    onChange={(e) =>
                      setEmailData({
                        ...emailData,
                        reportTypes: Array.from(e.target.selectedOptions, (o) => o.value),
                      })
                    }
                    className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
                    size={4}
                  >
                    <option value="B2B">B2B Report</option>
                    <option value="B2C">B2C Report</option>
                    <option value="B2C_DETAIL">B2C Detail Report</option>
                    <option value="All">All Reports Combined</option>
                  </select>
                  <p className="text-xs text-[#6B7FA0] mt-1">Hold Ctrl/Cmd to select multiple</p>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-[#F8F9FB] border-t border-[#E8ECF2] px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 py-2 border border-[#E8ECF2] rounded-lg hover:bg-[#F8F9FB] transition text-[#0F1A3A]"
              >
                Cancel
              </button>
              <button
                onClick={sendEmailReport}
                disabled={sendingEmail}
                className="px-4 py-2 bg-[#0F1A3A] text-white rounded-lg hover:bg-[#1B2F5C] transition flex items-center"
              >
                {sendingEmail ? (
                  <>
                    <FiLoader className="h-4 w-4 animate-spin mr-2" /> Sending...
                  </>
                ) : (
                  <>
                    <FiSend className="h-4 w-4 mr-2" /> Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="p-8">
        {/* Header with Hotel Info */}
        <div className="bg-gradient-to-r from-[#0F1A3A] to-[#1B2F5C] rounded-2xl shadow-xl mb-8 overflow-hidden">
          <div className="px-8 py-6">
            <div className="flex flex-wrap justify-between items-start gap-4">
              <div className="flex items-center space-x-4">
                <div>
                  <h1 className="text-3xl font-bold text-white">{hotelInfo.name}</h1>
                  <p className="text-[#8A9FC0] mt-1">GST Returns & Compliance Dashboard</p>
                </div>
              </div>
              <div className="bg-white/10 rounded-xl px-6 py-3 backdrop-blur-sm border border-white/10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                  <div className="flex items-center text-gray-300 text-sm">
                    <FiInfo className="mr-2 h-3 w-3 text-[#C9A84C]" />
                    <span>GSTIN:</span>
                    <span className="ml-2 font-mono text-white">{hotelInfo.gstin}</span>
                  </div>
                  <div className="flex items-center text-gray-300 text-sm">
                    <FiMapPin className="mr-2 h-3 w-3 text-[#C9A84C]" />
                    <span>State Code:</span>
                    <span className="ml-2 font-mono text-white">{hotelInfo.stateCode}</span>
                  </div>
                  <div className="flex items-center text-gray-300 text-sm">
                    <FiMapPin className="mr-2 h-3 w-3 text-[#C9A84C]" />
                    <span>Address:</span>
                    <span className="ml-2 text-white text-xs">{hotelInfo.address}</span>
                  </div>
                  <div className="flex items-center text-gray-300 text-sm">
                    <FiPhone className="mr-2 h-3 w-3 text-[#C9A84C]" />
                    <span>Contact:</span>
                    <span className="ml-2 text-white">{hotelInfo.contact}</span>
                  </div>
                  <div className="flex items-center text-gray-300 text-sm">
                    <FiMailIcon className="mr-2 h-3 w-3 text-[#C9A84C]" />
                    <span>Email:</span>
                    <span className="ml-2 text-white">{hotelInfo.email}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Module Selection Tabs */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => {
              setModuleType('hotel');
              setActiveTab('B2B');
              setReportData(null);
              setSummaryData(null);
              setCurrentPage(1);
            }}
            className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
              moduleType === 'hotel'
                ? 'bg-[#0F1A3A] text-white shadow-lg'
                : 'bg-white text-[#4A5F7A] hover:bg-[#F8F9FB] border border-[#E8ECF2]'
            }`}
          >
            <FiHome className="h-4 w-4" />
            Hotel GST Reports
          </button>
          <button
            onClick={() => {
              setModuleType('restaurant');
              setActiveTab('B2B');
              setReportData(null);
              setSummaryData(null);
              setCurrentPage(1);
            }}
            className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
              moduleType === 'restaurant'
                ? 'bg-[#0F1A3A] text-white shadow-lg'
                : 'bg-white text-[#4A5F7A] hover:bg-[#F8F9FB] border border-[#E8ECF2]'
            }`}
          >
            <FiCoffee className="h-4 w-4" />
            Restaurant GST Reports
          </button>
        </div>

        {/* Summary Cards */}
        {renderSummaryCards()}

        {/* Quick Actions Bar */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6 border border-[#E8ECF2]">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <FiSend className="text-[#C9A84C]" />
              <span className="text-sm font-medium text-[#0F1A3A]">Quick Actions</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={downloadCombinedReport}
                className="px-4 py-2 bg-[#C9A84C] text-[#0F1A3A] text-sm rounded-lg hover:bg-[#B8973A] transition flex items-center font-medium"
              >
                <FiDownloadCloud className="mr-2 h-4 w-4" /> Combined Report
              </button>
              <button
                onClick={() => downloadReport('xlsx')}
                className="px-4 py-2 bg-[#0F1A3A] text-white text-sm rounded-lg hover:bg-[#1B2F5C] transition flex items-center"
              >
                <FiDownload className="mr-2 h-4 w-4" /> Export {activeTab}
              </button>
              <button
                onClick={() => setShowEmailModal(true)}
                className="px-4 py-2 bg-[#1B2F5C] text-white text-sm rounded-lg hover:bg-[#2A4580] transition flex items-center"
              >
                <FiMail className="mr-2 h-4 w-4" /> Email Report
              </button>
              <button
                onClick={copySummary}
                className="px-4 py-2 bg-[#6B7FA0] text-white text-sm rounded-lg hover:bg-[#4A5F7A] transition flex items-center"
              >
                <FiCopy className="mr-2 h-4 w-4" /> Copy Summary
              </button>
              {/* <button
                onClick={() => window.print()}
                className="px-4 py-2 bg-[#6B7FA0] text-white text-sm rounded-lg hover:bg-[#4A5F7A] transition flex items-center"
              >
                <FiPrinter className="mr-2 h-4 w-4" /> Print
              </button> */}
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6 border border-[#E8ECF2]">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <FiCalendar className="text-[#6B7FA0]" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
              />
              <span className="text-[#6B7FA0]">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
              />
            </div>

            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6B7FA0]" />
              <input
                type="text"
                value={filters.invoiceNo}
                onChange={(e) => setFilters({ ...filters, invoiceNo: e.target.value })}
                placeholder="Search by invoice number (e.g., P//26-27/ 00140)..."
                className="w-full pl-10 pr-4 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
              />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 border rounded-lg text-sm flex items-center transition ${
                showFilters
                  ? 'bg-[#0F1A3A] text-white border-[#0F1A3A]'
                  : 'hover:bg-[#F8F9FB] border-[#E8ECF2] text-[#0F1A3A]'
              }`}
            >
              <FiFilter className="mr-2 h-4 w-4" /> Filters
            </button>

            <button
              onClick={fetchReportData}
              className="px-4 py-2 bg-[#0F1A3A] text-white rounded-lg text-sm hover:bg-[#1B2F5C] transition flex items-center"
            >
              <FiRefreshCw className="mr-2 h-4 w-4" /> Generate
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-[#E8ECF2]">
              <div>
                <label className="block text-xs text-[#6B7FA0] mb-1 font-medium">
                  GSTIN Number
                </label>
                <input
                  type="text"
                  value={filters.gstin}
                  onChange={(e) => setFilters({ ...filters, gstin: e.target.value })}
                  placeholder="Enter GSTIN"
                  className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-[#6B7FA0] mb-1 font-medium">
                  GST Rate (%)
                </label>
                <select
                  value={filters.gstRate}
                  onChange={(e) => setFilters({ ...filters, gstRate: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
                >
                  <option value="">All Rates</option>
                  <option value="0">0%</option>
                  <option value="2">2%</option>
                  <option value="5">5%</option>
                  <option value="12">12%</option>
                  <option value="13">13%</option>
                  <option value="18">18%</option>
                  <option value="28">28%</option>
                </select>
              </div>
              {moduleType === 'hotel' && (
                <>
                  <div>
                    <label className="block text-xs text-[#6B7FA0] mb-1 font-medium">Company</label>
                    <select
                      value={filters.companyId}
                      onChange={(e) => setFilters({ ...filters, companyId: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
                    >
                      <option value="">All Companies</option>
                      {companies.map((c) => (
                        <option key={c.company_id} value={c.company_id}>
                          {c.company_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#6B7FA0] mb-1 font-medium">
                      Payment Status
                    </label>
                    <select
                      value={filters.paymentStatus}
                      onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
                    >
                      <option value="">All Status</option>
                      <option value="Paid">Paid</option>
                      <option value="Pending">Pending</option>
                      <option value="Partial">Partial</option>
                    </select>
                  </div>
                </>
              )}
              {moduleType === 'restaurant' && (
                <div>
                  <label className="block text-xs text-[#6B7FA0] mb-1 font-medium">
                    Order Type
                  </label>
                  <select
                    value={filters.orderType}
                    onChange={(e) => setFilters({ ...filters, orderType: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
                  >
                    <option value="">All Types</option>
                    <option value="dining">Dine-In</option>
                    <option value="parcel">Parcel</option>
                    <option value="swiggy">Swiggy</option>
                    <option value="zomato">Zomato</option>
                    <option value="other_platform">Other Platform</option>
                  </select>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 border-b border-[#E8ECF2]">
          <button
            onClick={() => {
              setActiveTab('B2B');
              setCurrentPage(1);
            }}
            className={`px-6 py-3 text-sm font-medium transition-all ${
              activeTab === 'B2B'
                ? 'border-b-2 border-[#C9A84C] text-[#0F1A3A]'
                : 'text-[#6B7FA0] hover:text-[#0F1A3A]'
            }`}
          >
            B2B Invoices
          </button>
          <button
            onClick={() => {
              setActiveTab('B2C');
              setCurrentPage(1);
            }}
            className={`px-6 py-3 text-sm font-medium transition-all ${
              activeTab === 'B2C'
                ? 'border-b-2 border-[#C9A84C] text-[#0F1A3A]'
                : 'text-[#6B7FA0] hover:text-[#0F1A3A]'
            }`}
          >
            B2C Summary
          </button>
          <button
            onClick={() => {
              setActiveTab('B2C_DETAIL');
              setCurrentPage(1);
            }}
            className={`px-6 py-3 text-sm font-medium transition-all ${
              activeTab === 'B2C_DETAIL'
                ? 'border-b-2 border-[#C9A84C] text-[#0F1A3A]'
                : 'text-[#6B7FA0] hover:text-[#0F1A3A]'
            }`}
          >
            B2C Detailed
          </button>
        </div>

        {/* Main Content Area */}
        {renderContent()}

        {/* Pagination */}
        {renderPagination()}
      </div>
    </div>
  );
}

export default GSTReportsModule;

// import React, { useState, useEffect } from 'react';
// import { saveAs } from 'file-saver';
// import {
//   FiCalendar,
//   FiSearch,
//   FiDownload,
//   FiRefreshCw,
//   FiFileText,
//   FiMail,
//   FiPrinter,
//   FiFilter,
//   FiDownloadCloud,
//   FiCheckCircle,
//   FiCopy,
//   FiChevronLeft,
//   FiChevronRight,
//   FiChevronsLeft,
//   FiChevronsRight,
//   FiX,
//   FiLoader,
//   FiTrendingUp,
//   FiUsers,
//   FiFile,
//   FiSend,
//   FiBarChart2,
//   FiAlertCircle,
//   FiHome,
//   FiMapPin,
//   FiPhone,
//   FiMail as FiMailIcon,
//   FiInfo,
//   FiCreditCard,
//   FiCoffee,
// } from 'react-icons/fi';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL || 'http://localhost:9000';

// function GSTReportsModule() {
//   // Module Type: 'hotel' or 'restaurant'
//   const [moduleType, setModuleType] = useState('hotel');

//   // UI State
//   const [activeTab, setActiveTab] = useState('B2B');
//   const [showFilters, setShowFilters] = useState(false);
//   const [showEmailModal, setShowEmailModal] = useState(false);
//   const [copySuccess, setCopySuccess] = useState(false);
//   const [summaryData, setSummaryData] = useState(null);

//   // Date State
//   const [startDate, setStartDate] = useState(() => {
//     const date = new Date();
//     date.setMonth(date.getMonth() - 1);
//     return date.toISOString().split('T')[0];
//   });
//   const [endDate, setEndDate] = useState(() => {
//     const date = new Date();
//     return date.toISOString().split('T')[0];
//   });

//   // Filter State
//   const [filters, setFilters] = useState({
//     gstin: '',
//     gstRate: '',
//     companyId: '',
//     invoiceNo: '',
//     paymentStatus: '',
//     orderType: '',
//   });

//   // Email State
//   const [emailData, setEmailData] = useState({
//     recipients: '',
//     subject: 'GST Returns Report - Pagoda Executive',
//     message:
//       'Dear Team,\n\nPlease find attached the GST returns report for the selected period.\n\nRegards,\nAccounts Team\nPagoda Executive',
//     format: 'xlsx',
//     reportTypes: ['B2B', 'B2C', 'B2C_DETAIL'],
//   });

//   // Loading States
//   const [loading, setLoading] = useState(false);
//   const [sendingEmail, setSendingEmail] = useState(false);
//   const [emailSent, setEmailSent] = useState(false);

//   // Data States
//   const [reportData, setReportData] = useState(null);
//   const [companies, setCompanies] = useState([]);

//   // Pagination
//   const [currentPage, setCurrentPage] = useState(1);
//   const [itemsPerPage] = useState(20);

//   // Hotel Info
//   const hotelInfo = {
//     name: 'The Pagoda Executive',
//     gstin: '27AAACP1234K1Z5',
//     address: 'Lote MIDC, Chiplun - 415722, Maharashtra',
//     contact: '+91 8698732336',
//     email: 'pagodamain@gmail.com',
//     stateCode: '27',
//   };

//   // Fetch Companies
//   useEffect(() => {
//     fetchCompanies();
//   }, []);

//   // Fetch Report Data on filter change
//   useEffect(() => {
//     const timer = setTimeout(() => {
//       fetchReportData();
//     }, 500);
//     return () => clearTimeout(timer);
//   }, [
//     startDate,
//     endDate,
//     filters.invoiceNo,
//     filters.gstin,
//     filters.gstRate,
//     filters.companyId,
//     filters.paymentStatus,
//     filters.orderType,
//     moduleType,
//     activeTab,
//   ]);

//   // Reset page when tab changes
//   useEffect(() => {
//     setCurrentPage(1);
//   }, [activeTab, moduleType]);

//   const fetchCompanies = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/companies/list`);
//       if (response.ok) {
//         const data = await response.json();
//         setCompanies(data.companies || []);
//       }
//     } catch (error) {
//       console.error('Error fetching companies:', error);
//     }
//   };

//   const fetchSummaryData = async () => {
//     try {
//       let url;
//       if (moduleType === 'hotel') {
//         url = new URL(`${BQ_BASE_URL}/bq/api/gst-reports/summary`);
//       } else {
//         url = new URL(`${BQ_BASE_URL}/bq/api/restaurant-gst-reports/summary`);
//       }

//       if (startDate) url.searchParams.append('start_date', startDate);
//       if (endDate) url.searchParams.append('end_date', endDate);

//       // Fix: Properly encode invoice number search for summary
//       if (filters.invoiceNo) {
//         const invoiceSearch = filters.invoiceNo.trim();
//         url.searchParams.append('invoice_number', encodeURIComponent(invoiceSearch));
//       }

//       if (filters.gstin) url.searchParams.append('customer_gst', filters.gstin);
//       if (filters.gstRate) url.searchParams.append('gst_rate', parseFloat(filters.gstRate));
//       if (filters.paymentStatus && moduleType === 'hotel')
//         url.searchParams.append('payment_status', filters.paymentStatus);
//       if (filters.companyId && moduleType === 'hotel')
//         url.searchParams.append('company_id', filters.companyId);
//       if (filters.orderType && moduleType === 'restaurant')
//         url.searchParams.append('order_type', filters.orderType);

//       url.searchParams.append('page', '1');
//       url.searchParams.append('page_size', '100');

//       const response = await fetch(url.toString());

//       if (response.ok) {
//         const data = await response.json();
//         if (moduleType === 'hotel') {
//           setSummaryData({
//             grand_total: data.grand_total || 0,
//             total_taxable_value: data.total_taxable_value || 0,
//             total_gst: data.total_gst || 0,
//             total_cgst: data.total_cgst || 0,
//             total_sgst: data.total_sgst || 0,
//             total_orders: data.total_invoices || 0,
//             b2b_orders: data.b2b_invoices || 0,
//             b2c_orders: data.b2c_invoices || 0,
//           });
//         } else {
//           setSummaryData(data);
//         }
//       }
//     } catch (error) {
//       console.error('Error fetching summary data:', error);
//     }
//   };

//   const fetchReportData = async () => {
//     setLoading(true);
//     try {
//       let url;
//       if (moduleType === 'hotel') {
//         url = new URL(`${BQ_BASE_URL}/bq/api/gst-reports/preview`);
//       } else {
//         url = new URL(`${BQ_BASE_URL}/bq/api/restaurant-gst-reports/preview`);
//       }

//       if (startDate) url.searchParams.append('start_date', startDate);
//       if (endDate) url.searchParams.append('end_date', endDate);

//       // Fix: Properly encode invoice number search
//       if (filters.invoiceNo) {
//         const invoiceSearch = filters.invoiceNo.trim();
//         // URL encode the search value to handle special characters like / and spaces
//         url.searchParams.append('invoice_number', encodeURIComponent(invoiceSearch));
//       }

//       if (filters.gstin) url.searchParams.append('customer_gst', filters.gstin);
//       if (filters.gstRate) url.searchParams.append('gst_rate', parseFloat(filters.gstRate));
//       if (filters.paymentStatus && moduleType === 'hotel')
//         url.searchParams.append('payment_status', filters.paymentStatus);
//       if (filters.companyId && moduleType === 'hotel')
//         url.searchParams.append('company_id', filters.companyId);
//       if (filters.orderType && moduleType === 'restaurant')
//         url.searchParams.append('order_type', filters.orderType);

//       if (moduleType !== 'hotel') {
//         url.searchParams.append('report_type', activeTab);
//       }

//       url.searchParams.append('page', '1');
//       url.searchParams.append('page_size', '500');

//       const response = await fetch(url.toString());

//       if (!response.ok) {
//         const errorText = await response.text();
//         console.error('API Error:', errorText);
//         throw new Error(`HTTP ${response.status}: ${errorText}`);
//       }

//       const data = await response.json();
//       setReportData(data);

//       fetchSummaryData();
//     } catch (error) {
//       console.error('Error fetching report data:', error);
//       alert('Failed to fetch report data. Please check the console for details.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const getCurrentRows = () => {
//     if (!reportData) return [];

//     if (moduleType === 'hotel') {
//       switch (activeTab) {
//         case 'B2B':
//           return reportData.b2b_data?.service_rate_groups?.flatMap((g) => g.rows) || [];
//         case 'B2C':
//           return reportData.b2c_data?.rows || [];
//         case 'B2C_DETAIL':
//           return reportData.b2c_detail_data?.service_rate_groups?.flatMap((g) => g.rows) || [];
//         default:
//           return [];
//       }
//     } else {
//       switch (activeTab) {
//         case 'B2B':
//           return reportData.b2b_data?.service_rate_groups?.flatMap((g) => g.rows) || [];
//         case 'B2C':
//           return reportData.b2c_data?.rows || [];
//         case 'B2C_DETAIL':
//           return reportData.b2c_detail_data?.service_rate_groups?.flatMap((g) => g.rows) || [];
//         default:
//           return [];
//       }
//     }
//   };

//   const getCurrentSummary = () => {
//     if (!reportData) return {};
//     if (moduleType === 'hotel') {
//       switch (activeTab) {
//         case 'B2B':
//           return reportData.b2b_data || {};
//         case 'B2C':
//           return reportData.b2c_data || {};
//         case 'B2C_DETAIL':
//           return reportData.b2c_detail_data || {};
//         default:
//           return {};
//       }
//     } else {
//       switch (activeTab) {
//         case 'B2B':
//           return reportData.b2b_data || {};
//         case 'B2C':
//           return reportData.b2c_data || {};
//         case 'B2C_DETAIL':
//           return reportData.b2c_detail_data || {};
//         default:
//           return {};
//       }
//     }
//   };

//   const currentRows = getCurrentRows();
//   const totalPages = Math.ceil(currentRows.length / itemsPerPage);
//   const paginatedRows = currentRows.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   const goToPage = (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));
//   const goToPreviousPage = () => goToPage(currentPage - 1);
//   const goToNextPage = () => goToPage(currentPage + 1);
//   const goToFirstPage = () => setCurrentPage(1);
//   const goToLastPage = () => setCurrentPage(totalPages);

//   const downloadReport = async (format = 'xlsx') => {
//     setLoading(true);
//     try {
//       let url;
//       if (moduleType === 'hotel') {
//         url = new URL(`${BQ_BASE_URL}/bq/api/gst-reports/generate`);
//       } else {
//         url = new URL(`${BQ_BASE_URL}/bq/api/restaurant-gst-reports/generate`);
//       }

//       url.searchParams.append('export_format', format);

//       if (startDate) url.searchParams.append('start_date', startDate);
//       if (endDate) url.searchParams.append('end_date', endDate);

//       // Fix: Properly encode invoice number search for download
//       if (filters.invoiceNo) {
//         const invoiceSearch = filters.invoiceNo.trim();
//         url.searchParams.append('invoice_number', encodeURIComponent(invoiceSearch));
//       }

//       if (activeTab !== 'ALL' && moduleType !== 'restaurant')
//         url.searchParams.append('report_type', activeTab);
//       if (moduleType === 'restaurant') url.searchParams.append('report_type', activeTab);
//       if (filters.gstin) url.searchParams.append('customer_gst', filters.gstin);
//       if (filters.gstRate) url.searchParams.append('gst_rate', parseFloat(filters.gstRate));
//       if (filters.paymentStatus && moduleType === 'hotel')
//         url.searchParams.append('payment_status', filters.paymentStatus);
//       if (filters.companyId && moduleType === 'hotel')
//         url.searchParams.append('company_id', filters.companyId);
//       if (filters.orderType && moduleType === 'restaurant')
//         url.searchParams.append('order_type', filters.orderType);

//       url.searchParams.append('page', '1');
//       url.searchParams.append('page_size', '500');

//       const response = await fetch(url.toString());

//       if (!response.ok) {
//         const errorText = await response.text();
//         throw new Error(`HTTP ${response.status}: ${errorText}`);
//       }

//       const blob = await response.blob();
//       const filename = `${moduleType.toUpperCase()}_GST_${activeTab}_${startDate}_to_${endDate}.${format}`;
//       saveAs(blob, filename);
//     } catch (error) {
//       console.error('Error downloading report:', error);
//       alert('Failed to download report. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const downloadCombinedReport = async () => {
//     setLoading(true);
//     try {
//       let url;
//       if (moduleType === 'hotel') {
//         url = new URL(`${BQ_BASE_URL}/bq/api/gst-reports/generate-combined`);
//       } else {
//         url = new URL(`${BQ_BASE_URL}/bq/api/restaurant-gst-reports/generate-combined`);
//       }

//       if (startDate) url.searchParams.append('start_date', startDate);
//       if (endDate) url.searchParams.append('end_date', endDate);

//       // Fix: Properly encode invoice number search for combined download
//       if (filters.invoiceNo) {
//         const invoiceSearch = filters.invoiceNo.trim();
//         url.searchParams.append('invoice_number', encodeURIComponent(invoiceSearch));
//       }

//       if (filters.gstin) url.searchParams.append('customer_gst', filters.gstin);
//       if (filters.gstRate) url.searchParams.append('gst_rate', parseFloat(filters.gstRate));
//       if (filters.paymentStatus && moduleType === 'hotel')
//         url.searchParams.append('payment_status', filters.paymentStatus);
//       if (filters.companyId && moduleType === 'hotel')
//         url.searchParams.append('company_id', filters.companyId);
//       if (filters.orderType && moduleType === 'restaurant')
//         url.searchParams.append('order_type', filters.orderType);

//       url.searchParams.append('page', '1');
//       url.searchParams.append('page_size', '500');

//       const response = await fetch(url.toString());

//       if (!response.ok) {
//         const errorText = await response.text();
//         throw new Error(`HTTP ${response.status}: ${errorText}`);
//       }

//       const blob = await response.blob();
//       const filename = `${moduleType.toUpperCase()}_GST_Combined_${startDate}_to_${endDate}.xlsx`;
//       saveAs(blob, filename);
//     } catch (error) {
//       console.error('Error downloading combined report:', error);
//       alert('Failed to download combined report. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };
//   const sendEmailReport = async () => {
//     if (!emailData.recipients.trim()) {
//       alert('Please enter at least one recipient email address');
//       return;
//     }

//     setSendingEmail(true);
//     const recipientList = emailData.recipients
//       .split(',')
//       .map((e) => e.trim())
//       .filter((e) => e);

//     const emailFilters = {};
//     if (startDate) emailFilters.start_date = startDate;
//     if (endDate) emailFilters.end_date = endDate;
//     if (filters.invoiceNo) emailFilters.invoice_number = filters.invoiceNo;
//     if (filters.gstin) emailFilters.customer_gst = filters.gstin;
//     if (filters.gstRate) emailFilters.gst_rate = parseFloat(filters.gstRate);
//     if (filters.companyId && moduleType === 'hotel') emailFilters.company_id = filters.companyId;
//     if (filters.orderType && moduleType === 'restaurant')
//       emailFilters.order_type = filters.orderType;

//     const payload = {
//       recipients: recipientList,
//       subject: emailData.subject,
//       message: emailData.message,
//       export_format: emailData.format,
//       report_types: emailData.reportTypes,
//       filters: emailFilters,
//     };

//     try {
//       const url =
//         moduleType === 'hotel'
//           ? `${BQ_BASE_URL}/bq/api/gst-reports/send-email`
//           : `${BQ_BASE_URL}/bq/api/restaurant-gst-reports/send-email`;

//       const response = await fetch(url, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: JSON.stringify(payload),
//       });

//       const result = await response.json();

//       if (response.ok) {
//         setEmailSent(true);
//         setShowEmailModal(false);
//         setEmailData({
//           ...emailData,
//           recipients: '',
//         });
//         setTimeout(() => setEmailSent(false), 3000);
//       } else {
//         alert(`Failed to send email: ${result.detail || 'Unknown error'}`);
//       }
//     } catch (error) {
//       console.error('Error sending email:', error);
//       alert('Error sending email. Please try again.');
//     } finally {
//       setSendingEmail(false);
//     }
//   };

//   const copySummary = () => {
//     const summary = summaryData || {};
//     const summaryText = `═══════════════════════════════════════════════════════════════════════════════
//                          PAGODA EXECUTIVE - GST RETURN SUMMARY
// ═══════════════════════════════════════════════════════════════════════════════
//  Module: ${moduleType.toUpperCase()}
//  Period: ${startDate} to ${endDate}
// ═══════════════════════════════════════════════════════════════════════════════
//  💰 GRAND TOTAL: ₹${summary.grand_total?.toLocaleString() || '0'}
//  📊 Total Taxable Value: ₹${summary.total_taxable_value?.toLocaleString() || '0'}
//  💰 Total GST: ₹${summary.total_gst?.toLocaleString() || '0'}
//    • CGST: ₹${summary.total_cgst?.toLocaleString() || '0'}
//    • SGST: ₹${summary.total_sgst?.toLocaleString() || '0'}
//  📄 Total Invoices: ${summary.total_orders?.toLocaleString() || '0'}
// ═══════════════════════════════════════════════════════════════════════════════
//  Generated from Pagoda Executive ERP System on ${new Date().toLocaleString()}`;

//     navigator.clipboard.writeText(summaryText);
//     setCopySuccess(true);
//     setTimeout(() => setCopySuccess(false), 2000);
//   };

//   const renderSummaryCards = () => {
//     const summary = summaryData || {};

//     const cards = [
//       {
//         title: 'Grand Total',
//         value: `₹${summary.grand_total?.toLocaleString() || '0'}`,
//         subtext: 'Including all taxes',
//         icon: <FiFile className="text-[#C9A84C] text-xl" />,
//       },
//       {
//         title: 'Total Taxable Value',
//         value: `₹${summary.total_taxable_value?.toLocaleString() || '0'}`,
//         subtext: 'Value before tax',
//         icon: <FiTrendingUp className="text-[#C9A84C] text-xl" />,
//       },
//       {
//         title: 'Total GST',
//         value: `₹${summary.total_gst?.toLocaleString() || '0'}`,
//         subtext: `CGST: ₹${summary.total_cgst?.toFixed(2) || '0'} | SGST: ₹${summary.total_sgst?.toFixed(2) || '0'}`,
//         icon: <FiBarChart2 className="text-[#C9A84C] text-xl" />,
//       },
//       {
//         title: 'Total Orders/Invoices',
//         value: summary.total_orders?.toLocaleString() || '0',
//         subtext:
//           moduleType === 'hotel'
//             ? `B2B: ${summary.b2b_orders || 0} | B2C: ${summary.b2c_orders || 0}`
//             : `Restaurant Orders`,
//         icon: <FiCreditCard className="text-[#C9A84C] text-xl" />,
//       },
//     ];

//     return (
//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
//         {cards.map((card, idx) => (
//           <div
//             key={idx}
//             className="bg-white rounded-xl shadow-md p-5 border border-[#E8ECF2] hover:shadow-lg transition-shadow"
//           >
//             <div className="flex justify-between items-start">
//               <div>
//                 <p className="text-xs text-[#6B7FA0] uppercase tracking-wide font-semibold">
//                   {card.title}
//                 </p>
//                 <p className="text-2xl font-bold text-[#0F1A3A] mt-2">{card.value}</p>
//                 <p className="text-xs text-[#6B7FA0] mt-2">{card.subtext}</p>
//               </div>
//               <div className="bg-[#F0F2F5] p-3 rounded-xl">{card.icon}</div>
//             </div>
//           </div>
//         ))}
//       </div>
//     );
//   };

//   const renderB2BTable = () => {
//     const summary = getCurrentSummary();

//     return (
//       <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-[#E8ECF2]">
//         <div className="px-6 py-4 bg-[#0F1A3A]">
//           <div className="flex justify-between items-center">
//             <div>
//               <h3 className="text-white font-semibold">B2B Invoice Details</h3>
//               <p className="text-[#8A9FC0] text-xs mt-1">
//                 Date Range: {summary.date_range || `${startDate} to ${endDate}`}
//               </p>
//             </div>
//             <div className="flex space-x-2">
//               <span className="px-3 py-1 bg-[#C9A84C] text-[#0F1A3A] text-xs rounded-full font-medium">
//                 Recipients: {summary.no_of_recipients || 0}
//               </span>
//               <span className="px-3 py-1 bg-[#1B2F5C] text-white text-xs rounded-full">
//                 Invoices: {summary.no_of_invoices || 0}
//               </span>
//             </div>
//           </div>
//         </div>
//         <div className="overflow-x-auto">
//           <table className="min-w-full divide-y divide-[#E8ECF2]">
//             <thead className="bg-[#F8F9FB]">
//               <tr>
//                 <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   GSTIN
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Receiver Name
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Invoice No
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Date
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Service Type
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Rate
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Taxable Value
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   CGST
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   SGST
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Total
//                 </th>
//               </tr>
//             </thead>
//             <tbody className="divide-y divide-[#E8ECF2]">
//               {paginatedRows.map((row, idx) => (
//                 <tr key={idx} className="hover:bg-[#F8F9FB] transition-colors">
//                   <td className="px-4 py-3 text-sm font-mono text-[#0F1A3A]">
//                     {row.gstin_uin || '-'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-[#4A5F7A]">
//                     {row.receiver_name || row.order_type_label || '-'}
//                   </td>
//                   <td className="px-4 py-3 text-sm font-medium text-[#0F1A3A]">
//                     {row.invoice_number || '-'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-[#4A5F7A]">{row.invoice_date || '-'}</td>
//                   <td className="px-4 py-3 text-sm">
//                     <span className="px-2 py-1 bg-[#F0F2F5] rounded-full text-xs text-[#4A5F7A]">
//                       {row.service_type || row.order_type_label || '-'}
//                     </span>
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right font-medium text-[#0F1A3A]">
//                     {row.rate || 0}%
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
//                     ₹{row.taxable_value?.toFixed(2) || '0.00'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
//                     ₹{row.cgst?.toFixed(2) || '0.00'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
//                     ₹{row.sgst?.toFixed(2) || '0.00'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right font-semibold text-[#0F1A3A]">
//                     ₹{row.total_inv?.toFixed(2) || row.total?.toFixed(2) || '0.00'}
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//             <tfoot className="bg-[#F8F9FB] font-semibold">
//               <tr>
//                 <td colSpan="5" className="px-4 py-3 text-right text-[#0F1A3A]">
//                   Total:
//                 </td>
//                 <td className="px-4 py-3 text-right text-[#0F1A3A]">
//                   ₹{summary.total_taxable_value?.toFixed(2) || '0.00'}
//                 </td>
//                 <td className="px-4 py-3 text-right text-[#0F1A3A]">
//                   ₹{summary.total_cgst?.toFixed(2) || '0.00'}
//                 </td>
//                 <td className="px-4 py-3 text-right text-[#0F1A3A]">
//                   ₹{summary.total_sgst?.toFixed(2) || '0.00'}
//                 </td>
//                 <td className="px-4 py-3 text-right text-[#0F1A3A]">
//                   ₹
//                   {summary.total_invoice_value?.toFixed(2) ||
//                     summary.grand_total?.toFixed(2) ||
//                     '0.00'}
//                 </td>
//               </tr>
//             </tfoot>
//           </table>
//         </div>
//       </div>
//     );
//   };

//   const renderB2CTable = () => {
//     const summary = getCurrentSummary();

//     return (
//       <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-[#E8ECF2]">
//         <div className="px-6 py-4 bg-[#0F1A3A]">
//           <div>
//             <h3 className="text-white font-semibold">B2C Summary (Consumer Sales)</h3>
//             <p className="text-[#8A9FC0] text-xs mt-1">
//               Date Range: {summary.date_range || `${startDate} to ${endDate}`}
//             </p>
//           </div>
//         </div>
//         <div className="overflow-x-auto">
//           <table className="min-w-full divide-y divide-[#E8ECF2]">
//             <thead className="bg-[#F8F9FB]">
//               <tr>
//                 <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Type
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Category/Place
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Place of Supply
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Rate
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Taxable Value
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   CGST
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   SGST
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Total
//                 </th>
//               </tr>
//             </thead>
//             <tbody className="divide-y divide-[#E8ECF2]">
//               {paginatedRows.map((row, idx) => (
//                 <tr key={idx} className="hover:bg-[#F8F9FB]">
//                   <td className="px-4 py-3 text-sm">
//                     <span className="px-2 py-1 text-[#0F1A3A] rounded-full text-xs bg-[#F0F2F5]">
//                       {row.order_type_label || row.row_type || '-'}
//                     </span>
//                   </td>
//                   <td className="px-4 py-3 text-sm text-[#4A5F7A]">{row.category || ''}</td>
//                   <td className="px-4 py-3 text-sm text-[#4A5F7A]">
//                     {row.place_of_supply || '27-Maharashtra'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right font-medium text-[#0F1A3A]">
//                     {row.rate || 0}%
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
//                     ₹{row.taxable_value?.toFixed(2) || '0.00'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
//                     ₹{row.cgst?.toFixed(2) || '0.00'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
//                     ₹{row.sgst?.toFixed(2) || '0.00'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right font-semibold text-[#0F1A3A]">
//                     ₹{row.total?.toFixed(2) || '0.00'}
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//             <tfoot className="bg-[#F8F9FB] font-semibold">
//               <tr>
//                 <td colSpan="3" className="px-4 py-3 text-right text-[#0F1A3A]">
//                   Total:
//                 </td>
//                 <td className="px-4 py-3 text-right text-[#0F1A3A]">
//                   ₹{summary.total_taxable_value?.toFixed(2) || '0.00'}
//                 </td>
//                 <td className="px-4 py-3 text-right text-[#0F1A3A]">
//                   ₹{summary.total_cgst?.toFixed(2) || '0.00'}
//                 </td>
//                 <td className="px-4 py-3 text-right text-[#0F1A3A]">
//                   ₹{summary.total_sgst?.toFixed(2) || '0.00'}
//                 </td>
//                 <td className="px-4 py-3 text-right text-[#0F1A3A]">
//                   ₹{summary.grand_total?.toFixed(2) || '0.00'}
//                 </td>
//               </tr>
//             </tfoot>
//           </table>
//         </div>
//       </div>
//     );
//   };

//   const renderB2CDetailTable = () => {
//     const summary = getCurrentSummary();

//     return (
//       <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-[#E8ECF2]">
//         <div className="px-6 py-4 bg-[#0F1A3A]">
//           <div className="flex justify-between items-center">
//             <div>
//               <h3 className="text-white font-semibold">B2C Detailed Invoice Report</h3>
//               <p className="text-[#8A9FC0] text-xs mt-1">
//                 Date Range: {summary.date_range || `${startDate} to ${endDate}`}
//               </p>
//             </div>
//             <span className="px-3 py-1 bg-[#C9A84C] text-[#0F1A3A] text-xs rounded-full font-medium">
//               Transactions: {summary.no_of_invoices || summary.no_of_orders || 0}
//             </span>
//           </div>
//         </div>
//         <div className="overflow-x-auto">
//           <table className="min-w-full divide-y divide-[#E8ECF2]">
//             <thead className="bg-[#F8F9FB]">
//               <tr>
//                 <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   GSTIN
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Category/Type
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Invoice No
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Date
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Rate
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Taxable Value
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   CGST
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   SGST
//                 </th>
//                 <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7FA0] uppercase tracking-wider">
//                   Total
//                 </th>
//               </tr>
//             </thead>
//             <tbody className="divide-y divide-[#E8ECF2]">
//               {paginatedRows.map((row, idx) => (
//                 <tr key={idx} className="hover:bg-[#F8F9FB]">
//                   <td className="px-4 py-3 text-sm font-mono text-[#0F1A3A]">
//                     {row.gstin_uin || 'N/A'}
//                   </td>
//                   <td className="px-4 py-3 text-sm">
//                     <span className="px-2 py-1 bg-[#F0F2F5] rounded-full text-xs text-[#4A5F7A]">
//                       {row.category || row.order_type_label || '-'}
//                     </span>
//                   </td>
//                   <td className="px-4 py-3 text-sm font-medium text-[#0F1A3A]">
//                     {row.invoice_number || '-'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-[#4A5F7A]">{row.invoice_date || '-'}</td>
//                   <td className="px-4 py-3 text-sm text-right font-medium text-[#0F1A3A]">
//                     {row.rate || 0}%
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
//                     ₹{row.taxable_value?.toFixed(2) || '0.00'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
//                     ₹{row.cgst?.toFixed(2) || '0.00'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right text-[#4A5F7A]">
//                     ₹{row.sgst?.toFixed(2) || '0.00'}
//                   </td>
//                   <td className="px-4 py-3 text-sm text-right font-semibold text-[#0F1A3A]">
//                     ₹{row.total_inv?.toFixed(2) || row.total?.toFixed(2) || '0.00'}
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//             <tfoot className="bg-[#F8F9FB] font-semibold">
//               <tr>
//                 <td colSpan="4" className="px-4 py-3 text-right text-[#0F1A3A]">
//                   Total:
//                 </td>
//                 <td className="px-4 py-3 text-right text-[#0F1A3A]">
//                   ₹{summary.total_taxable_value?.toFixed(2) || '0.00'}
//                 </td>
//                 <td className="px-4 py-3 text-right text-[#0F1A3A]">
//                   ₹{summary.total_cgst?.toFixed(2) || '0.00'}
//                 </td>
//                 <td className="px-4 py-3 text-right text-[#0F1A3A]">
//                   ₹{summary.total_sgst?.toFixed(2) || '0.00'}
//                 </td>
//                 <td className="px-4 py-3 text-right text-[#0F1A3A]">
//                   ₹
//                   {summary.total_invoice_value?.toFixed(2) ||
//                     summary.grand_total?.toFixed(2) ||
//                     '0.00'}
//                 </td>
//               </tr>
//             </tfoot>
//           </table>
//         </div>
//       </div>
//     );
//   };

//   const renderPagination = () => {
//     if (totalPages <= 1) return null;

//     const getPageNumbers = () => {
//       const pages = [];
//       const maxVisible = 5;
//       let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
//       let endPage = Math.min(totalPages, startPage + maxVisible - 1);

//       if (endPage - startPage + 1 < maxVisible) {
//         startPage = Math.max(1, endPage - maxVisible + 1);
//       }

//       for (let i = startPage; i <= endPage; i++) {
//         pages.push(i);
//       }
//       return pages;
//     };

//     return (
//       <div className="bg-white rounded-xl shadow-lg px-6 py-4 mt-6 flex items-center justify-between flex-wrap gap-4 border border-[#E8ECF2]">
//         <div className="text-sm text-[#6B7FA0]">
//           Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
//           {Math.min(currentPage * itemsPerPage, currentRows.length)} of {currentRows.length} entries
//         </div>
//         <div className="flex items-center space-x-2">
//           <button
//             onClick={goToFirstPage}
//             disabled={currentPage === 1}
//             className={`p-2 border border-[#E8ECF2] rounded-lg transition ${
//               currentPage === 1
//                 ? 'text-[#D0D5DD] cursor-not-allowed'
//                 : 'hover:bg-[#F8F9FB] text-[#0F1A3A]'
//             }`}
//           >
//             <FiChevronsLeft className="h-4 w-4" />
//           </button>
//           <button
//             onClick={goToPreviousPage}
//             disabled={currentPage === 1}
//             className={`p-2 border border-[#E8ECF2] rounded-lg transition ${
//               currentPage === 1
//                 ? 'text-[#D0D5DD] cursor-not-allowed'
//                 : 'hover:bg-[#F8F9FB] text-[#0F1A3A]'
//             }`}
//           >
//             <FiChevronLeft className="h-4 w-4" />
//           </button>

//           {getPageNumbers().map((page) => (
//             <button
//               key={page}
//               onClick={() => goToPage(page)}
//               className={`px-3 py-1.5 border rounded-lg text-sm transition ${
//                 currentPage === page
//                   ? 'bg-[#0F1A3A] text-white border-[#0F1A3A]'
//                   : 'hover:bg-[#F8F9FB] text-[#0F1A3A] border-[#E8ECF2]'
//               }`}
//             >
//               {page}
//             </button>
//           ))}

//           <button
//             onClick={goToNextPage}
//             disabled={currentPage === totalPages}
//             className={`p-2 border border-[#E8ECF2] rounded-lg transition ${
//               currentPage === totalPages
//                 ? 'text-[#D0D5DD] cursor-not-allowed'
//                 : 'hover:bg-[#F8F9FB] text-[#0F1A3A]'
//             }`}
//           >
//             <FiChevronRight className="h-4 w-4" />
//           </button>
//           <button
//             onClick={goToLastPage}
//             disabled={currentPage === totalPages}
//             className={`p-2 border border-[#E8ECF2] rounded-lg transition ${
//               currentPage === totalPages
//                 ? 'text-[#D0D5DD] cursor-not-allowed'
//                 : 'hover:bg-[#F8F9FB] text-[#0F1A3A]'
//             }`}
//           >
//             <FiChevronsRight className="h-4 w-4" />
//           </button>
//         </div>
//         <div className="text-sm text-[#6B7FA0]">
//           Page {currentPage} of {totalPages}
//         </div>
//       </div>
//     );
//   };

//   const renderContent = () => {
//     if (!reportData) {
//       return (
//         <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl shadow-lg border border-[#E8ECF2]">
//           <FiFileText className="text-[#D0D5DD] text-6xl mb-4" />
//           <p className="text-[#6B7FA0]">Select date range and click Generate to view report</p>
//         </div>
//       );
//     }

//     if (currentRows.length === 0) {
//       return (
//         <div className="text-center py-16 bg-white rounded-xl shadow-lg border border-[#E8ECF2]">
//           <FiAlertCircle className="h-16 w-16 mx-auto text-[#D0D5DD] mb-4" />
//           <p className="text-[#6B7FA0]">No data found for the selected period</p>
//           <p className="text-sm text-[#D0D5DD] mt-1">Try adjusting your date range or filters</p>
//         </div>
//       );
//     }

//     switch (activeTab) {
//       case 'B2B':
//         return renderB2BTable();
//       case 'B2C':
//         return renderB2CTable();
//       case 'B2C_DETAIL':
//         return renderB2CDetailTable();
//       default:
//         return null;
//     }
//   };

//   return (
//     <div className="min-h-screen bg-[#F4F6FA]">
//       {/* Loading Overlay */}
//       {loading && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-xl p-6 shadow-xl flex items-center space-x-3 border border-[#E8ECF2]">
//             <FiLoader className="h-6 w-6 animate-spin text-[#C9A84C]" />
//             <span className="text-[#0F1A3A]">Loading report data...</span>
//           </div>
//         </div>
//       )}

//       {/* Notifications */}
//       {emailSent && (
//         <div className="fixed top-4 right-4 bg-[#0F1A3A] text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center">
//           <FiCheckCircle className="h-5 w-5 mr-2 text-[#C9A84C]" />
//           Email sent successfully!
//         </div>
//       )}

//       {copySuccess && (
//         <div className="fixed top-4 right-4 bg-[#0F1A3A] text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center">
//           <FiCheckCircle className="h-5 w-5 mr-2 text-[#C9A84C]" />
//           Summary copied to clipboard!
//         </div>
//       )}

//       {/* Email Modal */}
//       {showEmailModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#E8ECF2]">
//             <div className="sticky top-0 bg-white border-b border-[#E8ECF2] px-6 py-4 flex justify-between items-center">
//               <h3 className="text-xl font-bold text-[#0F1A3A]">Send GST Report via Email</h3>
//               <button
//                 onClick={() => setShowEmailModal(false)}
//                 className="p-2 hover:bg-[#F8F9FB] rounded-lg text-[#6B7FA0]"
//               >
//                 <FiX className="h-5 w-5" />
//               </button>
//             </div>
//             <div className="p-6 space-y-4">
//               <div>
//                 <label className="block text-sm font-medium text-[#0F1A3A] mb-1">
//                   Recipients *
//                 </label>
//                 <input
//                   type="text"
//                   value={emailData.recipients}
//                   onChange={(e) => setEmailData({ ...emailData, recipients: e.target.value })}
//                   placeholder="email1@example.com, email2@example.com"
//                   className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//                 />
//                 <p className="text-xs text-[#6B7FA0] mt-1">Separate multiple emails with commas</p>
//               </div>
//               <div>
//                 <label className="block text-sm font-medium text-[#0F1A3A] mb-1">Subject</label>
//                 <input
//                   type="text"
//                   value={emailData.subject}
//                   onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
//                   className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium text-[#0F1A3A] mb-1">Message</label>
//                 <textarea
//                   rows="4"
//                   value={emailData.message}
//                   onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
//                   className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//                 />
//               </div>
//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-sm font-medium text-[#0F1A3A] mb-1">Format</label>
//                   <select
//                     value={emailData.format}
//                     onChange={(e) => setEmailData({ ...emailData, format: e.target.value })}
//                     className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//                   >
//                     <option value="xlsx">Excel (.xlsx)</option>
//                     <option value="pdf">PDF (.pdf)</option>
//                     <option value="csv">CSV (.csv)</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-[#0F1A3A] mb-1">
//                     Report Types
//                   </label>
//                   <select
//                     multiple
//                     value={emailData.reportTypes}
//                     onChange={(e) =>
//                       setEmailData({
//                         ...emailData,
//                         reportTypes: Array.from(e.target.selectedOptions, (o) => o.value),
//                       })
//                     }
//                     className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//                     size={4}
//                   >
//                     <option value="B2B">B2B Report</option>
//                     <option value="B2C">B2C Report</option>
//                     <option value="B2C_DETAIL">B2C Detail Report</option>
//                     <option value="All">All Reports Combined</option>
//                   </select>
//                   <p className="text-xs text-[#6B7FA0] mt-1">Hold Ctrl/Cmd to select multiple</p>
//                 </div>
//               </div>
//             </div>
//             <div className="sticky bottom-0 bg-[#F8F9FB] border-t border-[#E8ECF2] px-6 py-4 flex justify-end space-x-3">
//               <button
//                 onClick={() => setShowEmailModal(false)}
//                 className="px-4 py-2 border border-[#E8ECF2] rounded-lg hover:bg-[#F8F9FB] transition text-[#0F1A3A]"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={sendEmailReport}
//                 disabled={sendingEmail}
//                 className="px-4 py-2 bg-[#0F1A3A] text-white rounded-lg hover:bg-[#1B2F5C] transition flex items-center"
//               >
//                 {sendingEmail ? (
//                   <>
//                     <FiLoader className="h-4 w-4 animate-spin mr-2" /> Sending...
//                   </>
//                 ) : (
//                   <>
//                     <FiSend className="h-4 w-4 mr-2" /> Send Email
//                   </>
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Main Content */}
//       <div className="p-8">
//         {/* Header with Hotel Info */}
//         <div className="bg-gradient-to-r from-[#0F1A3A] to-[#1B2F5C] rounded-2xl shadow-xl mb-8 overflow-hidden">
//           <div className="px-8 py-6">
//             <div className="flex flex-wrap justify-between items-start gap-4">
//               <div className="flex items-center space-x-4">
//                 <div>
//                   <h1 className="text-3xl font-bold text-white">{hotelInfo.name}</h1>
//                   <p className="text-[#8A9FC0] mt-1">GST Returns & Compliance Dashboard</p>
//                 </div>
//               </div>
//               <div className="bg-white/10 rounded-xl px-6 py-3 backdrop-blur-sm border border-white/10">
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
//                   <div className="flex items-center text-gray-300 text-sm">
//                     <FiInfo className="mr-2 h-3 w-3 text-[#C9A84C]" />
//                     <span>GSTIN:</span>
//                     <span className="ml-2 font-mono text-white">{hotelInfo.gstin}</span>
//                   </div>
//                   <div className="flex items-center text-gray-300 text-sm">
//                     <FiMapPin className="mr-2 h-3 w-3 text-[#C9A84C]" />
//                     <span>State Code:</span>
//                     <span className="ml-2 font-mono text-white">{hotelInfo.stateCode}</span>
//                   </div>
//                   <div className="flex items-center text-gray-300 text-sm">
//                     <FiMapPin className="mr-2 h-3 w-3 text-[#C9A84C]" />
//                     <span>Address:</span>
//                     <span className="ml-2 text-white text-xs">{hotelInfo.address}</span>
//                   </div>
//                   <div className="flex items-center text-gray-300 text-sm">
//                     <FiPhone className="mr-2 h-3 w-3 text-[#C9A84C]" />
//                     <span>Contact:</span>
//                     <span className="ml-2 text-white">{hotelInfo.contact}</span>
//                   </div>
//                   <div className="flex items-center text-gray-300 text-sm">
//                     <FiMailIcon className="mr-2 h-3 w-3 text-[#C9A84C]" />
//                     <span>Email:</span>
//                     <span className="ml-2 text-white">{hotelInfo.email}</span>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Module Selection Tabs */}
//         <div className="flex space-x-4 mb-6">
//           <button
//             onClick={() => {
//               setModuleType('hotel');
//               setActiveTab('B2B');
//               setReportData(null);
//               setSummaryData(null);
//               setCurrentPage(1);
//             }}
//             className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
//               moduleType === 'hotel'
//                 ? 'bg-[#0F1A3A] text-white shadow-lg'
//                 : 'bg-white text-[#4A5F7A] hover:bg-[#F8F9FB] border border-[#E8ECF2]'
//             }`}
//           >
//             <FiHome className="h-4 w-4" />
//             Hotel GST Reports
//           </button>
//           <button
//             onClick={() => {
//               setModuleType('restaurant');
//               setActiveTab('B2B');
//               setReportData(null);
//               setSummaryData(null);
//               setCurrentPage(1);
//             }}
//             className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
//               moduleType === 'restaurant'
//                 ? 'bg-[#0F1A3A] text-white shadow-lg'
//                 : 'bg-white text-[#4A5F7A] hover:bg-[#F8F9FB] border border-[#E8ECF2]'
//             }`}
//           >
//             <FiCoffee className="h-4 w-4" />
//             Restaurant GST Reports
//           </button>
//         </div>

//         {/* Summary Cards */}
//         {renderSummaryCards()}

//         {/* Quick Actions Bar */}
//         <div className="bg-white rounded-xl shadow-lg p-4 mb-6 border border-[#E8ECF2]">
//           <div className="flex flex-wrap items-center justify-between gap-4">
//             <div className="flex items-center space-x-3">
//               <FiSend className="text-[#C9A84C]" />
//               <span className="text-sm font-medium text-[#0F1A3A]">Quick Actions</span>
//             </div>
//             <div className="flex flex-wrap gap-3">
//               <button
//                 onClick={downloadCombinedReport}
//                 className="px-4 py-2 bg-[#C9A84C] text-[#0F1A3A] text-sm rounded-lg hover:bg-[#B8973A] transition flex items-center font-medium"
//               >
//                 <FiDownloadCloud className="mr-2 h-4 w-4" /> Combined Report
//               </button>
//               <button
//                 onClick={() => downloadReport('xlsx')}
//                 className="px-4 py-2 bg-[#0F1A3A] text-white text-sm rounded-lg hover:bg-[#1B2F5C] transition flex items-center"
//               >
//                 <FiDownload className="mr-2 h-4 w-4" /> Export {activeTab}
//               </button>
//               <button
//                 onClick={() => setShowEmailModal(true)}
//                 className="px-4 py-2 bg-[#1B2F5C] text-white text-sm rounded-lg hover:bg-[#2A4580] transition flex items-center"
//               >
//                 <FiMail className="mr-2 h-4 w-4" /> Email Report
//               </button>
//               <button
//                 onClick={copySummary}
//                 className="px-4 py-2 bg-[#6B7FA0] text-white text-sm rounded-lg hover:bg-[#4A5F7A] transition flex items-center"
//               >
//                 <FiCopy className="mr-2 h-4 w-4" /> Copy Summary
//               </button>
//               <button
//                 onClick={() => window.print()}
//                 className="px-4 py-2 bg-[#6B7FA0] text-white text-sm rounded-lg hover:bg-[#4A5F7A] transition flex items-center"
//               >
//                 <FiPrinter className="mr-2 h-4 w-4" /> Print
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Filters Bar */}
//         <div className="bg-white rounded-xl shadow-lg p-4 mb-6 border border-[#E8ECF2]">
//           <div className="flex flex-wrap items-center gap-4">
//             <div className="flex items-center space-x-2">
//               <FiCalendar className="text-[#6B7FA0]" />
//               <input
//                 type="date"
//                 value={startDate}
//                 onChange={(e) => setStartDate(e.target.value)}
//                 className="px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//               />
//               <span className="text-[#6B7FA0]">to</span>
//               <input
//                 type="date"
//                 value={endDate}
//                 onChange={(e) => setEndDate(e.target.value)}
//                 className="px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//               />
//             </div>

//             <div className="flex-1 relative">
//               <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6B7FA0]" />
//               <input
//                 type="text"
//                 value={filters.invoiceNo}
//                 onChange={(e) => setFilters({ ...filters, invoiceNo: e.target.value })}
//                 placeholder="Search by invoice number..."
//                 className="w-full pl-10 pr-4 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//               />
//             </div>

//             <button
//               onClick={() => setShowFilters(!showFilters)}
//               className={`px-4 py-2 border rounded-lg text-sm flex items-center transition ${
//                 showFilters
//                   ? 'bg-[#0F1A3A] text-white border-[#0F1A3A]'
//                   : 'hover:bg-[#F8F9FB] border-[#E8ECF2] text-[#0F1A3A]'
//               }`}
//             >
//               <FiFilter className="mr-2 h-4 w-4" /> Filters
//             </button>

//             <button
//               onClick={fetchReportData}
//               className="px-4 py-2 bg-[#0F1A3A] text-white rounded-lg text-sm hover:bg-[#1B2F5C] transition flex items-center"
//             >
//               <FiRefreshCw className="mr-2 h-4 w-4" /> Generate
//             </button>
//           </div>

//           {/* Advanced Filters */}
//           {showFilters && (
//             <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-[#E8ECF2]">
//               <div>
//                 <label className="block text-xs text-[#6B7FA0] mb-1 font-medium">
//                   GSTIN Number
//                 </label>
//                 <input
//                   type="text"
//                   value={filters.gstin}
//                   onChange={(e) => setFilters({ ...filters, gstin: e.target.value })}
//                   placeholder="Enter GSTIN"
//                   className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//                 />
//               </div>
//               <div>
//                 <label className="block text-xs text-[#6B7FA0] mb-1 font-medium">
//                   GST Rate (%)
//                 </label>
//                 <select
//                   value={filters.gstRate}
//                   onChange={(e) => setFilters({ ...filters, gstRate: e.target.value })}
//                   className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//                 >
//                   <option value="">All Rates</option>
//                   <option value="0">0%</option>
//                   <option value="2">2%</option>
//                   <option value="5">5%</option>
//                   <option value="12">12%</option>
//                   <option value="13">13%</option>
//                   <option value="18">18%</option>
//                   <option value="28">28%</option>
//                 </select>
//               </div>
//               {moduleType === 'hotel' && (
//                 <>
//                   <div>
//                     <label className="block text-xs text-[#6B7FA0] mb-1 font-medium">Company</label>
//                     <select
//                       value={filters.companyId}
//                       onChange={(e) => setFilters({ ...filters, companyId: e.target.value })}
//                       className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//                     >
//                       <option value="">All Companies</option>
//                       {companies.map((c) => (
//                         <option key={c.company_id} value={c.company_id}>
//                           {c.company_name}
//                         </option>
//                       ))}
//                     </select>
//                   </div>
//                   <div>
//                     <label className="block text-xs text-[#6B7FA0] mb-1 font-medium">
//                       Payment Status
//                     </label>
//                     <select
//                       value={filters.paymentStatus}
//                       onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
//                       className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//                     >
//                       <option value="">All Status</option>
//                       <option value="Paid">Paid</option>
//                       <option value="Pending">Pending</option>
//                       <option value="Partial">Partial</option>
//                     </select>
//                   </div>
//                 </>
//               )}
//               {moduleType === 'restaurant' && (
//                 <div>
//                   <label className="block text-xs text-[#6B7FA0] mb-1 font-medium">
//                     Order Type
//                   </label>
//                   <select
//                     value={filters.orderType}
//                     onChange={(e) => setFilters({ ...filters, orderType: e.target.value })}
//                     className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg text-sm focus:ring-2 focus:ring-[#C9A84C] focus:border-[#C9A84C] outline-none"
//                   >
//                     <option value="">All Types</option>
//                     <option value="dining">Dine-In</option>
//                     <option value="parcel">Parcel</option>
//                     <option value="swiggy">Swiggy</option>
//                     <option value="zomato">Zomato</option>
//                     <option value="other_platform">Other Platform</option>
//                   </select>
//                 </div>
//               )}
//             </div>
//           )}
//         </div>

//         {/* Tabs */}
//         <div className="flex space-x-2 mb-6 border-b border-[#E8ECF2]">
//           <button
//             onClick={() => {
//               setActiveTab('B2B');
//               setCurrentPage(1);
//             }}
//             className={`px-6 py-3 text-sm font-medium transition-all ${
//               activeTab === 'B2B'
//                 ? 'border-b-2 border-[#C9A84C] text-[#0F1A3A]'
//                 : 'text-[#6B7FA0] hover:text-[#0F1A3A]'
//             }`}
//           >
//             B2B Invoices
//           </button>
//           <button
//             onClick={() => {
//               setActiveTab('B2C');
//               setCurrentPage(1);
//             }}
//             className={`px-6 py-3 text-sm font-medium transition-all ${
//               activeTab === 'B2C'
//                 ? 'border-b-2 border-[#C9A84C] text-[#0F1A3A]'
//                 : 'text-[#6B7FA0] hover:text-[#0F1A3A]'
//             }`}
//           >
//             B2C Summary
//           </button>
//           <button
//             onClick={() => {
//               setActiveTab('B2C_DETAIL');
//               setCurrentPage(1);
//             }}
//             className={`px-6 py-3 text-sm font-medium transition-all ${
//               activeTab === 'B2C_DETAIL'
//                 ? 'border-b-2 border-[#C9A84C] text-[#0F1A3A]'
//                 : 'text-[#6B7FA0] hover:text-[#0F1A3A]'
//             }`}
//           >
//             B2C Detailed
//           </button>
//         </div>

//         {/* Main Content Area */}
//         {renderContent()}

//         {/* Pagination */}
//         {renderPagination()}
//       </div>
//     </div>
//   );
// }

// export default GSTReportsModule;
